import { PanelFooter } from '@Components/PanelFooter';
import { AUFTRAG_API } from '@Constants/backendConstants';
import type { ApiStorageTransaction } from '@Model';
import {
    Button,
    EditFrameProvider,
    FlexColumn,
    FrameHeader,
    type IEditFrame,
    useGuiBinder,
} from '@synqo/frontend-component-library';
import { useCallback, useContext, useRef, useState } from 'react';
import { BookingFlowContext } from './bookingFlowContext';
import { type BookingInput, bookingErrors, withRejection } from './bookingRules';
import { StockPreviewLine } from './StockPreviewLine';
import { StorageTransactionBookingSection } from './Sections/StorageTransactionBookingSection';
import classes from './StorageTransactionBookingBody.module.css';

interface IProps {
    frame: IEditFrame<ApiStorageTransaction>;
    /** What the server refused the booking for, empty once the booking changed. */
    rejection: string | undefined;
}

function readLatest(frame: IEditFrame<ApiStorageTransaction>): Promise<ApiStorageTransaction> {
    return new Promise((resolve) => {
        void frame.requestUpdateState(async (latest) => {
            resolve(latest);
            return undefined;
        });
    });
}

const isComplete = (booking: ApiStorageTransaction) =>
    !!booking.storage_location.foreign_key && !!booking.material.foreign_key;

// @ai-generated 2026-10-23T16:30:00+00:00 by matthias.gessinger@synqony.com using Claude Sonnet 5.5
export function StorageTransactionBookingBody({ frame, rejection }: IProps) {
    const booking = frame.data;
    const { requestNext, focusMaterial } = useContext(BookingFlowContext);
    const [submitted, setSubmitted] = useState(false);
    const [quantityEntered, setQuantityEntered] = useState(false);
    const [quantityTyped, setQuantityTyped] = useState(false);
    const quantityTypedRef = useRef(false);
    const markQuantityTyped = useCallback(() => {
        quantityTypedRef.current = true;
        setQuantityTyped(true);
    }, []);
    const markQuantityConfirmed = useCallback(() => setQuantityEntered(true), []);

    const binder = useGuiBinder<ApiStorageTransaction>({
        data: booking,
        meta: frame.meta,
        canEdit: true,
        requestUpdateState: frame.requestUpdateState,
    });

    const inputOf = (current: ApiStorageTransaction, typed: boolean): BookingInput => ({
        type: current.type,
        quantity: current.quantity,
        quantityEntered: typed,
        occurredAt: current.occurred_at,
    });
    const errors = withRejection(
        submitted ? bookingErrors(inputOf(booking, quantityTyped)) : {},
        rejection,
        booking.occurred_at,
    );
    const pending = frame.pendingAction !== undefined;

    const book = async (next: boolean) => {
        setSubmitted(true);
        const latest = await readLatest(frame);
        const found = bookingErrors(inputOf(latest, quantityTypedRef.current));
        if (found.occurredAt || found.quantity || !isComplete(latest)) {
            return;
        }

        requestNext(next);
        await frame.events.onSave?.();
    };

    return (
        <EditFrameProvider
            frame={{ showEmptyRequired: frame.showEmptyRequired || submitted, unsavedChanges: frame.unsavedChanges }}
        >
            <FlexColumn gap="none" align="stretch">
                <FrameHeader
                    label="Lagerbuchung"
                    resourceLabel="Lagerbuchung"
                    onClose={frame.events.onClose}
                    hideUnavailableActions
                />
                <FlexColumn className={classes.content} align="stretch">
                    <StorageTransactionBookingSection
                        binder={binder}
                        baseApiObjectPath={`${AUFTRAG_API}/storage_transactions/${booking.id}`}
                        requestUpdateState={frame.requestUpdateState}
                        transactionType={booking.type}
                        quantityEntered={quantityEntered}
                        onQuantityTyped={markQuantityTyped}
                        onQuantityConfirmed={markQuantityConfirmed}
                        errors={errors}
                        focusMaterial={focusMaterial}
                        storageLocationId={booking.storage_location.foreign_key}
                        materialId={booking.material.foreign_key}
                        avvId={booking.avv.foreign_key}
                        occurredAt={booking.occurred_at}
                    />
                    <StockPreviewLine
                        storageLocationId={booking.storage_location.foreign_key}
                        materialId={booking.material.foreign_key}
                        avvId={booking.avv.foreign_key}
                        occurredAt={booking.occurred_at}
                        type={booking.type}
                        quantity={booking.quantity}
                    />
                </FlexColumn>
                <PanelFooter>
                    <Button appearance="outlined" locked={pending} onClick={() => book(true)}>
                        Buchen und nächste
                    </Button>
                    <Button variant="primary" locked={pending} onClick={() => book(false)}>
                        Buchen
                    </Button>
                </PanelFooter>
            </FlexColumn>
        </EditFrameProvider>
    );
}
